// eslint-disable-next-line max-classes-per-file
import {
  CatalogWidgetProps,
  CustomizationSpec,
} from '@/webapi/use-widget-catalog-api';
import { InputType } from '@/features/editor/widgets/custom-widget/inputs/shared/input-type';
import {
  EditorChangeKind,
  EditorDeclarativeBlock,
  HtmlTransformKind,
} from '@/webapi/use-experience-api';
import { useTranspiler } from '@/features/editor/context/use-transpiler';
import { deepEqual } from '@/utils/types';

export const VSLY_AUTO_GENERATE = `/* VISUALLY - AUTO GENERATED CONTENT */`;
const TRANSPARENT_IMAGE = `data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7`;

export function asVisualEditorChange(
  selector: string,
  visualProps: CatalogWidgetProps,
  initialHtml?: string,
  initialProps?: CatalogWidgetProps,
): EditorDeclarativeBlock {
  const { asCompoundChange } = useTranspiler();
  const { js, css } = compileVisualProps(visualProps, initialProps);
  const change = asCompoundChange(
    EditorChangeKind.EDIT_COMPOUND,
    HtmlTransformKind.REPLACE,
    selector,
    selector,
    ``,
    css,
    js,
  );
  change.initialHtml = initialHtml;
  return change;
}

export function compileVisualProps(
  visualProps: CatalogWidgetProps,
  initialProps: CatalogWidgetProps,
): {
  js: string;
  css: string;
} {
  const styles = new Stylesheet();
  const js = new Javascript();

  visualProps?.customizations?.forEach((cust, custId) => {
    cust.components.forEach((comp, compId) => {
      comp.specs.forEach((spec, specId) => {
        const originalSpec =
          initialProps.customizations[custId].components[compId].specs[specId];
        if (!deepEqual(originalSpec, spec)) {
          handleSpec(spec.key, spec, styles, js);
        }
      });
    });
  });

  return {
    js: js.toString(),
    css: styles.toString(),
  };
}

export function asVisualPropsChange(
  visualProps: Record<string, CustomizationSpec>,
): {
  js: string;
  css: string;
} {
  const styles = new Stylesheet();
  const js = new Javascript();

  Object.values(visualProps).forEach((spec) => {
    handleSpec(spec.key, spec, styles, js);
  });

  return {
    js: js.toString(),
    css: styles.toString(),
  };
}

function handleSpec(
  selector: string,
  spec: CustomizationSpec,
  styles: Stylesheet,
  js: Javascript,
) {
  if (spec.type === InputType.TEXT_EDIT) {
    js.changeText(
      selector,
      spec.values[`value`],
      spec?.values?.[`textNodeIdx`],
    );
  } else if (spec.type === InputType.CTA) {
    js.changeText(selector, spec.values[`content`][0][`content`]);
    js.changeAttr(selector, `href`, spec.values[`content`][0][`href`]);
  } else if (spec.type === InputType.LINK) {
    js.changeAttr(selector, `href`, spec.values[`value`]);
  } else if (spec.type === InputType.SIZE) {
    styles.css(selector, `height`, spec.values[`height`]);
    styles.css(selector, `width`, spec.values[`width`]);
    if (spec.values[`parent`]) {
      const parentSelector = spec.values[`parent`];
      styles.css(`${parentSelector}::before`, `height`, spec.values[`height`]);
    }
  } else if (spec.type === InputType.TYPOGRAPHY) {
    styles.css(selector, `color`, spec.values[`color`]);
    styles.css(selector, `font-size`, spec.values[`fontSize`]);
    styles.css(selector, `font-weight`, spec.values[`fontWeight`]);
    styles.css(selector, `font-family`, spec.values[`fontFamily`]);
    styles.css(selector, `text-decoration`, spec.values[`textDecoration`]);

    if (spec.values[`textAlignSelector`]) {
      styles.css(
        spec.values[`textAlignSelector`],
        `text-align`,
        spec.values[`textAlign`],
      );
    } else {
      styles.css(selector, `text-align`, spec.values[`textAlign`]);
    }
  } else if (spec.type === InputType.BACKGROUND) {
    if (spec.values[`mode`] === `video`) {
      handleVideoElement(selector, spec, styles, js);
    } else {
      if ((spec.values[`nodeType`] as string)?.includes(`src`)) {
        handleImageElement(selector, spec, styles, js);
      }
      if ((spec.values[`nodeType`] as string)?.includes(`background`)) {
        handleImageBackground(selector, spec, styles, js);
      } else {
        handleCtaBackground(selector, spec, styles);
      }
    }
  } else if (spec.type === InputType.MARGIN) {
    styles.css(selector, `margin`, spec.values[`value`]);
  } else if (spec.type === InputType.TEXT_SHADOW && spec.values[`enabled`]) {
    styles.css(selector, `text-shadow`, spec.values[`value`]);
  } else if (spec.type === InputType.BOX_SHADOW && spec.values[`enabled`]) {
    styles.css(selector, `box-shadow`, spec.values[`value`]);
  } else if (spec.type === InputType.BORDER) {
    styles.css(selector, `border-radius`, spec.values[`borderRadius`]);
    if (spec.values[`parent`]) {
      styles.css(
        spec.values[`parent`],
        `border-radius`,
        spec.values[`borderRadius`],
      );
    }
    if (spec.values[`enabled`]) {
      styles.css(selector, `border`, spec.values[`border`]);
    }
  }
}

function handleCtaBackground(
  selector: string,
  spec: CustomizationSpec,
  styles: Stylesheet,
) {
  styles.css(selector, `background-color`, spec.values[`value`]);
}

function handleImageBackground(
  selector: string,
  spec: CustomizationSpec,
  styles: Stylesheet,
  js: Javascript,
) {
  if (spec.values[`mode`] === `image`) {
    const src = spec.values[`value`] as string;
    js.changeStyle(selector, `backgroundImage`, src);
  } else if (spec.values[`mode`] === `color`) {
    js.changeStyle(selector, `backgroundImage`, `none`);
    const value = spec.values[`value`];
    styles.css(selector, `background-color`, value);
  } else {
    js.changeStyle(selector, `backgroundImage`, `none`);
    styles.css(selector, `background`, `none`);
  }
}

function handleVideoElement(
  selector: string,
  spec: CustomizationSpec,
  styles: Stylesheet,
  js: Javascript,
) {
  js.changeVideoSource(selector, spec.values[`value`]);
}

function handleImageElement(
  selector: string,
  spec: CustomizationSpec,
  styles: Stylesheet,
  js: Javascript,
) {
  if (spec.values[`mode`] === `image`) {
    const src = (spec.values[`value`] as string)
      .replaceAll(`url("`, ``)
      .replaceAll(`")`, ``);
    js.changeImageSource(selector, src);
  } else if (spec.values[`mode`] === `color`) {
    js.changeImageSource(TRANSPARENT_IMAGE, selector);
    const value = spec.values[`value`];
    styles.css(selector, `background-color`, value);
  } else {
    js.changeImageSource(selector, TRANSPARENT_IMAGE);
  }
}

class Stylesheet {
  private mmap: Record<string, any[]> = {};

  css(selector: string, key: string, value: string) {
    if (key && value) {
      if (!this.mmap[selector]) {
        this.mmap[selector] = [];
      }
      this?.mmap?.[selector]?.push(`${key}: ${value}!important;`);
    }
  }

  toString(): string {
    return `${VSLY_AUTO_GENERATE}\n${Object.entries(this.mmap)
      .map(
        ([k, v]) => `${k} {
        ${v?.join(`\n`)}
      }`,
      )
      .join(`\n`)}`;
  }
}

class Javascript {
  private lines: string[] = [
    `function retry(timeout, count, fn) {
        try {
            fn()
        } catch (ex) {
            if (count > 0) {
                setTimeout(function (){
                    retry(timeout, count-1, fn)
                })
            } else {
                console.error("loomi-editor", ex);
            }
        }
    };`,
  ];

  push(line: string) {
    this.lines.push(`retry(100, 100, function(){${line}})`);
  }

  changeText(selector: string, text: string, textNodeIdx?: number) {
    if (textNodeIdx === undefined) {
      this.push(
        `document.querySelector(\`${selector}\`).textContent = \`${text}\``,
      );
    } else {
      this.push(
        `document.querySelector(\`${selector}\`).childNodes[${textNodeIdx}].textContent = \`${text}\``,
      );
    }
  }

  changeAttr(selector: string, attr: string, value: string) {
    this.push(
      `document.querySelector(\`${selector}\`).setAttribute(\`${attr}\`, \`${value}\`)`,
    );
  }

  changeStyle(selector: string, attr: string, value: string) {
    this.push(
      `document.querySelector(\`${selector}\`).style.${attr} = \`${value}\` `,
    );
  }

  changeImageSource(selector: string, src: string) {
    this.push(`const img = document.querySelector(\`${selector}\`);
    img.removeAttribute("srcset");
    img.src=\`${src}\``);
  }

  changeVideoSource(selector: string, src: string) {
    this.push(`const vid = document.querySelector(\`${selector}\`);
    vid.innerHTML = "";
    vid.setAttribute("src", "${src}")
    `);
  }

  toString(): string {
    return `${VSLY_AUTO_GENERATE}\n${this.lines.join(`;\n`)}`;
  }
}
